import { digger } from '@cbre/digger';
import { Column } from '../models';

/**
 * Helper function for contextualizeColumnFormControlOptions pipe. It reduces the formControlOptions.parentControl
 * of a Column. It helps to make Column information more accurate when dealing with contextualized elements (items in
 * tables). Every time you climb down levels while treating data you need adjust the column information.
 * @param Column
 * @returns Column
 *
 * @example
 * // For example, if assuming you have a column like:
 * {
 *   field: 'a/b/c',
 *   formControlOptions: {
 *     type: 'control',
 *     name: 'c',
 *     parentControl: {
 *       name: 'a', type: 'array',
 *       parentControl: {
 *         name: 'b', type: 'array',
 *       }
 *     }
 *   }
 * }
 * // input builder is capable to generate form entries for this column by following the parentControl path.
 * // if you want to add a new entry to the table and you want to use the Column config to generate the formGroup
 * // you need to cut the context so the service don't generate again a form array 'a' in a new form group.
 * // after calling this helper Column will be reduced to
 *
 * {
 *   field: 'a/b/c', // <- if combined with contextualizeSingleColumnField field value would be 'b/c'
 *   formControlOptions: {
 *     type: 'control',
 *     name: 'c',
 *      parentControl: {
 *       name: 'b', type: 'array',
 *     }
 *   }
 * }
 * // with this change if you pass again the column to input builder service it will be able to return you the
 * // proper control for a dialog trying to add an entry to 'a'
 */
export function contextualizeColumnFormControlOptions(column: Column): Column {
  const { formControlOptions } = column;
  // get next parent
  let nextParentControlOptions = digger(formControlOptions, 'parentControl.children', { useJsonata: true });
  // if found then contextualize
  if (nextParentControlOptions != null) {
    [nextParentControlOptions] = Object.values(nextParentControlOptions);
  }
  // return contextualized parentControl value of column
  return {
    ...column,
    formControlOptions: { ...formControlOptions, parentControl: nextParentControlOptions },
    unit: column.unit != null ? contextualizeColumnFormControlOptions(column.unit) : column.unit,
  };
}
